import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material";
import {
  Box,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { columntype, dataLocalStorage, getLocalStorage, searchParamsToString } from "src/helper/publicFunction";
import { getPrivileges } from "src/helper/privileges";
import { getCharityHistory } from "src/queries/query";
import DynamicTable from "src/components/table";
import TableSkeleton from "src/components/skeletons/TableSkeleton";
import EmptyTable from "src/components/shared/EmptyTable";

const CharityHistory = () => {
  let dataPrivileges = getPrivileges();
  const [selected, setSelected] = useState([]);
  const [status, setStatus] = useState("all");
  const theme = useTheme();
  const isLightMode = theme.palette.mode === "light";

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [searchParams, setSearchParams] = useState({
    limit: rowsPerPage,
    page,
    status: status,
    sortBy: "name",
    sortDirection: "desc",
  });

  const { data: charity, isLoading: loadingData } = useQuery({
    queryKey: ["charity-history", searchParamsToString(searchParams)],
    queryFn: () =>
      getCharityHistory({ params: searchParamsToString(searchParams) }),
    staleTime: Infinity,
  });

  const charities = charity?.data;
  const totalCount = charity?.totalCount;

  const columns = [
    { type: columntype.text, name: "charity" },
    { type: columntype.text, name: "amount" },
    { type: columntype.text, name: "user" },
    { type: columntype.text, name: "order" },
  ];

  const headCells = [
    { id: "charity", label: "Charity", withSort: false },
    { id: "amount", label: "amount", withSort: false },
    { id: "user", label: "User", withSort: false },
    { id: "order", label: "Order Id", withSort: false },
  ];

  useEffect(() => {
    setSearchParams((prev) => ({
      ...prev,
      limit: rowsPerPage,
      page: page,
    }));
  }, [page, rowsPerPage]);

  return (
    <Box
      sx={{
        backgroundColor: "transparent",
        width: "100%",
      }}
    >
      <Box
        sx={{
          border: isLightMode ? "1px solid #dfe7ff" : "1px solid #333F55",
          borderRadius: "4px;",
          marginBottom: "30px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              md: "row",
            },
            alignItems: {
              xs: "center",
              md: "start",
            },
            justifyContent: "space-between",
            width: "100%",
            borderBottom: isLightMode
              ? "1px solid #dfe7ff"
              : "1px solid #333F55",
            padding: "20px",
            borderRadius: "0px",
          }}
        >
          <Typography
            variant="h1"
            sx={{
              color: isLightMode ? "#303030" : "#fff",
              fontWeight: "bold",
              fontSize: "1.44rem",
            }}
          >
            Charity History List
          </Typography>
        </Box>
        <Box
          sx={{
            padding: "20px",
            width: "100%",
            gap: "20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "start",
          }}
        >
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            sx={{ marginTop: "20px" }}
          >
            <Grid item>
              <FormControl>
                <InputLabel>Show</InputLabel>
                <Select
                  onChange={(event) => setRowsPerPage(event.target.value)}
                  defaultValue={10}
                  value={searchParams.limit ?? rowsPerPage}
                  label="Show"
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Search Box */}
            <Grid item>
              <TextField
                value={searchParams?.term}
                onChange={(e) => {
                  setSearchParams((prev) => ({
                    ...prev,
                    term: e.target.value,
                  }));
                  setPage(1);
                }}
                placeholder="Search"
                variant="outlined"
                size="small"
              />
            </Grid>
          </Grid>
          {loadingData ? (
            <TableSkeleton />
          ) : charities && charities.length > 0 ? (
            <DynamicTable
              modelName={"Charity"}
              onRequestSort={(tableName, type) => {
                if (tableName && type) {
                  setSearchParams((prev) => {
                    const newSortDirection =
                      prev.sortDirection === type
                        ? type === "asc"
                          ? "desc"
                          : "asc"
                        : type;
                    return {
                      ...prev,
                      sortBy: tableName,
                      sortDirection: newSortDirection,
                    };
                  });
                }
              }} // call api
              onPressDelete={(ListIds) => {}}
              onPressItem={(row) => {}}
              onPressAnyAction={(row, failedName, newData) => {}}
              onPressAdd={() => {}} // when press add new button show modal to add new item
              withSearch={false}
              loadingEditdatatype={false}
              items={charities} // all data
              columns={columns} // type of data
              headCells={headCells} // columns name
              page={page} // number of page from api
              setPage={setPage} // set number of page from api
              selected={selected} // selected items
              setSelected={setSelected} // set selected items
              isLoadingUpdateActive={loadingData}
              rowsPerPage={rowsPerPage} // number show Data
              setRowsPerPage={setRowsPerPage} // set number show data
              Listdropdown={[]} // list action with function
              withOption={false} // for remove action column
              loadingData={loadingData} // when load data from api
              rowsPerPageOptions={[5, 10, 25, 50]} // set option number of show data
              totalItem={totalCount} // total data without pagination
              withDeleteItem={false}
              withEditItem={false}
            />
          ) : (
            <div style={{ width: "100%", textAlign: "center" }}>
              <EmptyTable headCells={headCells} />
            </div>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CharityHistory;
